/**
 * RecordFormModal
 * ─────────────────────────────────────────────────────────────────
 * Used for both "Add Record" and "Edit Record" in DatasetView.
 *
 * Layout: two-panel full-screen overlay
 *   Left  (60%) — scrollable form sections
 *   Right (40%) — sticky react-leaflet map for coordinate picking
 */
import { useContext, useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, useMapEvents, Tooltip } from "react-leaflet";
import { BASEMAPS, type BasemapKey } from '../constants/basemaps';
import { Map as LeafletMap } from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  XMarkIcon, MapPinIcon, CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import axiosInstance from "../utils/axios";
import { AuthContext } from "../contexts/Auth/Auth.Context";

/* ── Types ─────────────────────────────────────────────────────────── */
export interface RecordData {
  id?: number;
  caseNo?: string;
  dateOfSeizure?: string;
  nameOfAccused?: string;
  nationality?: string;
  gender?: string;
  location?: string;
  country?: string;
  latitude?: number | null;
  longitude?: number | null;
  charge1?: string; charge2?: string; charge3?: string;
  charge4?: string; charge5?: string;
  alternativeCharge1?: string; alternativeCharge2?: string; alternativeCharge3?: string;
  species?: string;
  exhibit?: string;
  weight?: string;
  judgmentDate?: string;
  judgmentOutcome?: string;
  sentencing?: string;
  caseStatus?: string;
}

interface Props {
  mode: "add" | "edit";
  initial?: RecordData;
  onClose: () => void;
  onSaved: (record: RecordData) => void;
}

/* ── Constants ─────────────────────────────────────────────────────── */
const COUNTRIES = [
  "Kenya","Tanzania","Uganda","Ethiopia","Rwanda","Somalia",
  "Mozambique","Zambia","Zimbabwe","DRC","Malawi","South Africa",
  "Nigeria","Ghana",
];
const STATUSES  = ["Ongoing", "Concluded", "Absconded"];
const GENDERS   = ["Male", "Female", "Unknown"];

const EMPTY: RecordData = {
  caseNo: "", dateOfSeizure: "", nameOfAccused: "", nationality: "",
  gender: "", location: "", country: "Kenya",
  latitude: null, longitude: null,
  charge1: "", charge2: "", charge3: "", charge4: "", charge5: "",
  alternativeCharge1: "", alternativeCharge2: "", alternativeCharge3: "",
  species: "", exhibit: "", weight: "",
  judgmentDate: "", judgmentOutcome: "", sentencing: "", caseStatus: "Ongoing",
};

/* ── Sub-components ────────────────────────────────────────────────── */

/** Leaflet click-handler — fires the callback, renders nothing */
function MapClickHandler({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) });
  return null;
}

/** Styled label + input pair */
function Field({
  label, value, onChange, type = "text", placeholder = "", disabled = false,
}: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[9px] font-semibold uppercase tracking-[0.12em]" style={{ color: "rgba(255,255,255,0.3)" }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="px-3 py-2 rounded-lg text-xs text-white/80 placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-green-500/40 transition-all disabled:opacity-40"
        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}
      />
    </div>
  );
}

/** Styled label + textarea pair */
function TextArea({
  label, value, onChange, rows = 3,
}: {
  label: string; value: string; onChange: (v: string) => void; rows?: number;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[9px] font-semibold uppercase tracking-[0.12em]" style={{ color: "rgba(255,255,255,0.3)" }}>
        {label}
      </label>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-3 py-2 rounded-lg text-xs text-white/80 placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-green-500/40 resize-none transition-all"
        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}
      />
    </div>
  );
}

/** Styled label + select pair */
function Select({
  label, value, onChange, options,
}: {
  label: string; value: string; onChange: (v: string) => void; options: string[];
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[9px] font-semibold uppercase tracking-[0.12em]" style={{ color: "rgba(255,255,255,0.3)" }}>
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-3 py-2 rounded-lg text-xs text-white/80 focus:outline-none focus:ring-1 focus:ring-green-500/40 appearance-none cursor-pointer transition-all"
        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}
      >
        <option value="">— Select —</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

/** Section heading */
function Section({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-3 mt-6 mb-3">
      <p className="text-[9px] font-bold uppercase tracking-[0.18em]" style={{ color: "rgba(74,222,128,0.55)" }}>
        {title}
      </p>
      <div className="flex-1 h-px" style={{ background: "rgba(74,222,128,0.1)" }} />
    </div>
  );
}

/* ── Main modal ────────────────────────────────────────────────────── */
export default function RecordFormModal({ mode, initial, onClose, onSaved }: Props) {
  const { state } = useContext(AuthContext);
  const mapRef = useRef<LeafletMap | null>(null);

  const [form,    setForm]    = useState<RecordData>({ ...EMPTY, ...initial });
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [basemap, setBasemap] = useState<BasemapKey>('dark');

  /* Sync initial data when editing */
  useEffect(() => {
    setForm({ ...EMPTY, ...initial });
    setError(null);
    setSuccess(false);
  }, [initial]);

  /* Pan map when coordinates change */
  useEffect(() => {
    if (form.latitude != null && form.longitude != null && mapRef.current) {
      mapRef.current.setView([form.latitude, form.longitude], Math.max(mapRef.current.getZoom(), 10));
    }
  }, [form.latitude, form.longitude]);

  function set<K extends keyof RecordData>(key: K, value: RecordData[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleMapPick(lat: number, lng: number) {
    set("latitude",  Math.round(lat * 1e6) / 1e6);
    set("longitude", Math.round(lng * 1e6) / 1e6);
  }

  async function handleSave() {
    if (!form.nameOfAccused?.trim()) { setError("Name of accused is required."); return; }
    if (!form.caseStatus)            { setError("Case status is required.");      return; }
    if (!form.country)               { setError("Country is required.");          return; }

    setSaving(true);
    setError(null);

    const payload: any = { ...form };
    // strip undefined/empty strings for clean payload
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "" || payload[k] === undefined) delete payload[k];
    });

    try {
      const headers = { Authorization: `Bearer ${state.userToken}` };
      const res = mode === "add"
        ? await axiosInstance.post("/api/v1/seizure-cases", payload, { headers })
        : await axiosInstance.put(`/api/v1/seizure-cases/${form.id}`, payload, { headers });

      setSuccess(true);
      setTimeout(() => { onSaved(res.data); onClose(); }, 1200);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const mapCenter: [number, number] =
    form.latitude != null && form.longitude != null
      ? [form.latitude, form.longitude]
      : [-1.286389, 36.817223]; // Nairobi default

  return (
    /* Overlay */
    <div
      className="fixed inset-0 flex z-[1200]"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}
    >
      {/* Modal panel */}
      <div
        className="flex flex-col md:flex-row w-full h-full max-w-6xl mx-auto my-4 rounded-2xl overflow-hidden shadow-2xl"
        style={{ background: "#061a0e", border: "1px solid rgba(255,255,255,0.08)" }}
      >

        {/* ── LEFT: scrollable form ─────────────────────────────── */}
        <div
          className="flex-1 flex flex-col overflow-hidden"
          style={{ borderRight: "1px solid rgba(255,255,255,0.07)" }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-6 py-4 shrink-0"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.07)", background: "#052e16" }}
          >
            <div>
              <p className="text-white/90 text-sm font-bold tracking-wide">
                {mode === "add" ? "Add New Record" : "Edit Record"}
              </p>
              <p className="text-white/30 text-[10px] mt-0.5">
                {mode === "add" ? "Create a new seizure case entry" : `Editing record #${form.id}`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/5 transition-all"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Scrollable form body */}
          <div
            className="flex-1 overflow-y-auto px-6 pb-6"
            style={{ scrollbarWidth: "thin", scrollbarColor: "#14532d transparent" }}
          >
            {/* ── Case Info ────────────────────────────────── */}
            <Section title="Case Information" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Case No." value={form.caseNo ?? ""} onChange={(v) => set("caseNo", v)} placeholder="e.g. CID/123/2024" />
              <Field label="Date of Seizure" value={form.dateOfSeizure ?? ""} onChange={(v) => set("dateOfSeizure", v)} type="date" />
              <Select label="Case Status *" value={form.caseStatus ?? ""} onChange={(v) => set("caseStatus", v)} options={STATUSES} />
              <Select label="Country *" value={form.country ?? ""} onChange={(v) => set("country", v)} options={COUNTRIES} />
            </div>

            {/* ── Accused ──────────────────────────────────── */}
            <Section title="Accused" />
            <div className="flex flex-col gap-3">
              <Field label="Name of Accused *" value={form.nameOfAccused ?? ""} onChange={(v) => set("nameOfAccused", v)} placeholder="Full name(s)" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nationality" value={form.nationality ?? ""} onChange={(v) => set("nationality", v)} placeholder="e.g. Kenyan" />
                <Select label="Gender" value={form.gender ?? ""} onChange={(v) => set("gender", v)} options={GENDERS} />
              </div>
            </div>

            {/* ── Seizure Details ──────────────────────────── */}
            <Section title="Seizure Details" />
            <div className="flex flex-col gap-3">
              <Field label="Location" value={form.location ?? ""} onChange={(v) => set("location", v)} placeholder="e.g. Tsavo East National Park, Kenya" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Species" value={form.species ?? ""} onChange={(v) => set("species", v)} placeholder="e.g. Elephant, Ivory" />
                <Field label="Weight / Quantity" value={form.weight ?? ""} onChange={(v) => set("weight", v)} placeholder="e.g. 4.5 kg ivory" />
              </div>
              <TextArea label="Exhibit / Evidence" value={form.exhibit ?? ""} onChange={(v) => set("exhibit", v)} rows={2} />
            </div>

            {/* ── Charges ──────────────────────────────────── */}
            <Section title="Charges" />
            <div className="flex flex-col gap-3">
              <Field label="Charge 1" value={form.charge1 ?? ""} onChange={(v) => set("charge1", v)} placeholder="Primary charge" />
              <Field label="Charge 2" value={form.charge2 ?? ""} onChange={(v) => set("charge2", v)} />
              <Field label="Charge 3" value={form.charge3 ?? ""} onChange={(v) => set("charge3", v)} />
              <Field label="Alternative Charge 1" value={form.alternativeCharge1 ?? ""} onChange={(v) => set("alternativeCharge1", v)} />
              <Field label="Alternative Charge 2" value={form.alternativeCharge2 ?? ""} onChange={(v) => set("alternativeCharge2", v)} />
            </div>

            {/* ── Judgment ─────────────────────────────────── */}
            <Section title="Judgment & Outcome" />
            <div className="flex flex-col gap-3">
              <Field label="Judgment Date" value={form.judgmentDate ?? ""} onChange={(v) => set("judgmentDate", v)} type="date" />
              <TextArea label="Judgment Outcome" value={form.judgmentOutcome ?? ""} onChange={(v) => set("judgmentOutcome", v)} rows={2} />
              <TextArea label="Sentencing" value={form.sentencing ?? ""} onChange={(v) => set("sentencing", v)} rows={2} />
            </div>

            {/* Spacer */}
            <div className="h-4" />
          </div>
        </div>

        {/* ── RIGHT: sticky map + actions ──────────────────────────── */}
        <div className="w-full md:w-[380px] flex flex-col shrink-0">

          {/* Map section header */}
          <div
            className="px-5 py-3.5 shrink-0 flex items-center gap-2"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.07)", background: "#052e16" }}
          >
            <MapPinIcon className="w-4 h-4 text-green-400" />
            <div>
              <p className="text-white/80 text-xs font-semibold">Pin Location</p>
              <p className="text-white/30 text-[10px]">Click the map to set coordinates</p>
            </div>
          </div>

          {/* Map */}
          <div className="relative flex-1 min-h-0" style={{ minHeight: 280 }}>
            <MapContainer
              center={mapCenter}
              zoom={form.latitude != null ? 10 : 6}
              style={{ width: "100%", height: "100%", minHeight: 280 }}
              scrollWheelZoom
              ref={mapRef}
            >
              <TileLayer
                key={basemap}
                url={BASEMAPS[basemap].url}
                attribution={BASEMAPS[basemap].attribution}
                maxZoom={BASEMAPS[basemap].maxZoom}
              />
              <MapClickHandler onPick={handleMapPick} />
              {form.latitude != null && form.longitude != null && (
                <CircleMarker
                  center={[form.latitude, form.longitude]}
                  radius={10}
                  color="rgba(255,255,255,0.6)"
                  weight={2}
                  fillColor="rgba(74,222,128,0.85)"
                  fillOpacity={0.9}
                >
                  <Tooltip permanent direction="top" offset={[0, -14]} className="leaflet-dark-tip">
                    <span style={{ fontSize: 10, color: "#fff", background: "transparent" }}>
                      {form.latitude.toFixed(4)}, {form.longitude.toFixed(4)}
                    </span>
                  </Tooltip>
                </CircleMarker>
              )}
            </MapContainer>

            {/* Basemap toggle */}
            <div className="absolute top-2 right-2 z-[1000] flex rounded-lg overflow-hidden border border-white/15" style={{ background: 'rgba(4,31,14,0.88)', backdropFilter: 'blur(8px)' }}>
              {(['dark', 'street', 'satellite'] as BasemapKey[]).map((bm) => (
                <button key={bm} onClick={() => setBasemap(bm)} title={BASEMAPS[bm].label}
                  className={`px-2.5 py-1.5 text-[9px] font-semibold transition-all ${basemap === bm ? 'bg-green-700/50 text-green-300' : 'text-white/35 hover:text-white/65'}`}>
                  {bm === 'dark' ? '🌑' : bm === 'street' ? '🗺' : '🛰'} {BASEMAPS[bm].label}
                </button>
              ))}
            </div>

            {/* No-pin hint overlay */}
            {form.latitude == null && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
                style={{ background: "rgba(3,14,7,0.45)" }}
              >
                <MapPinIcon className="w-8 h-8 text-green-400/30 mb-2" />
                <p className="text-white/25 text-xs tracking-wide">Click map to pin location</p>
              </div>
            )}
          </div>

          {/* Coordinate readout */}
          <div
            className="px-5 py-3 shrink-0 grid grid-cols-2 gap-3"
            style={{ borderTop: "1px solid rgba(255,255,255,0.07)", background: "rgba(5,46,22,0.6)" }}
          >
            <div className="flex flex-col gap-1">
              <label htmlFor="latitude" className="text-[9px] uppercase tracking-[0.12em] font-semibold" style={{ color: "rgba(255,255,255,0.3)" }}>
                Latitude
              </label>
              <input
                id="latitude"
                type="number"
                step="0.000001"
                placeholder="Auto-filled by map"
                value={form.latitude ?? ""}
                onChange={(e) => set("latitude", e.target.value ? parseFloat(e.target.value) : null)}
                className="px-3 py-2 rounded-lg text-xs text-white/80 placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-green-500/40 tabular-nums"
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="longitude" className="text-[9px] uppercase tracking-[0.12em] font-semibold" style={{ color: "rgba(255,255,255,0.3)" }}>
                Longitude
              </label>
              <input
                id="longitude"
                type="number"
                step="0.000001"
                placeholder="Auto-filled by map"
                value={form.longitude ?? ""}
                onChange={(e) => set("longitude", e.target.value ? parseFloat(e.target.value) : null)}
                className="px-3 py-2 rounded-lg text-xs text-white/80 placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-green-500/40 tabular-nums"
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}
              />
            </div>
          </div>

          {/* Feedback */}
          {error && (
            <div
              className="mx-4 mt-2 flex items-start gap-2 px-3 py-2.5 rounded-lg"
              style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.22)" }}
            >
              <ExclamationCircleIcon className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <p className="text-xs text-red-400">{error}</p>
            </div>
          )}
          {success && (
            <div
              className="mx-4 mt-2 flex items-center gap-2 px-3 py-2.5 rounded-lg"
              style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.22)" }}
            >
              <CheckCircleIcon className="w-4 h-4 text-green-400 shrink-0" />
              <p className="text-xs text-green-400">
                {mode === "add" ? "Record created!" : "Record updated!"} Closing…
              </p>
            </div>
          )}

          {/* Action buttons */}
          <div
            className="flex items-center justify-end gap-2 px-5 py-4 shrink-0"
            style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}
          >
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg text-xs font-medium transition-all disabled:opacity-40"
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.55)",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || success}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-40"
              style={{
                background: "rgba(34,197,94,0.8)",
                border: "1px solid rgba(34,197,94,0.4)",
                color: "#fff",
              }}
            >
              {saving && (
                <svg width="12" height="12" viewBox="0 0 24 24" className="animate-spin">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
                </svg>
              )}
              {saving ? "Saving…" : mode === "add" ? "Create Record" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      {/* Tooltip CSS fix */}
      <style>{`
        .leaflet-tooltip.leaflet-dark-tip {
          background: rgba(4,31,14,0.9) !important;
          border: 1px solid rgba(74,222,128,0.3) !important;
          color: rgba(255,255,255,0.8) !important;
          border-radius: 6px !important;
          box-shadow: none !important;
          white-space: nowrap;
        }
        .leaflet-tooltip.leaflet-dark-tip::before {
          border-top-color: rgba(74,222,128,0.3) !important;
        }
      `}</style>
    </div>
  );
}
